import { ICON_ART_SIZE, PvpFlag, VISION_CAP_TILES, WORLD_TILE_PIXELS, badgeArtUnit, flagStateOf, pixelType } from "odyssey-shared";

/**
 * Where a nameplate's parts go (P1.9-T16, the export's Nameplate spec read through ADR-061's amendments): the
 * 8 px flag mark, or its locked variant, with the name 2 logical px to its right in the pixel face at 1x, the
 * mark's foot on the name's baseline, the plate centred over the character with the foot of the name's line 2
 * logical px above the head; a plate that draws the mark alone (Flag only, P1.9-T24) is the mark centred where
 * the plate stands. Every position is computed in device pixels from an anchor that is itself a whole
 * device pixel, then divided by the UI scale, so every part draws on the device grid whatever the scale and the
 * name's width, and nothing is snapped to logical pixels, which would make a plate judder where the UI scale and
 * the world zoom differ (ADR-061, "world-attached UI has a layer and a camera of its own").
 */

/**
 * Logical pixels between the flag mark, or its lock, and the name.
 */
export const FLAG_GAP = 2;

/**
 * Logical pixels between the foot of the name's line and the character's head.
 */
export const ABOVE_HEAD = 2;

/**
 * The flag mark's side in logical pixels.
 */
export const MARK = 8;

/**
 * The party mark's side in logical pixels (ADR-101 decision 24, amended for P2.13-T22): a filled circle with its dark
 * rim, drawn 2 px after the name.
 */
export const PARTY_DOT = 6;

/**
 * The name's line in the pixel face's nameplate role, in logical pixels: its height and its baseline measured
 * down from its top.
 */
export const LINE: { readonly height: number; readonly baseline: number } = Object.freeze({ height: pixelType("nameplate").lineHeight, baseline: pixelType("nameplate").baseline });

/**
 * The staff badge's box in logical pixels (ADR-112 decision 4.3), which sets the art's size at a UI scale (the whole
 * k = floor(12 d / 24) where one fits the art's visible 24 pixels, else the fraction that fits them to the box; `badgeArtUnit`)
 * and is the room a badge has in the kit's DOM places. On a plate the badge is laid out as what it shows, not as its box
 * (P1.9-T122).
 */
export const BADGE_BOX = 12;

/**
 * The transparent pixels each side of the 32 pixel art that every badge of the rank pack leaves clear, in art pixels. On a
 * plate they are part of the badge's room, so the visible art stands `FLAG_GAP` from the flag mark and not that and four
 * more.
 */
export const BADGE_ART_INSET = 4;

/**
 * The visible width and height of the badge's art, in art pixels: the 32 less an inset each side.
 */
export const BADGE_ART_CONTENT = ICON_ART_SIZE - 2 * BADGE_ART_INSET;

/**
 * The width a badge takes on a plate in logical pixels: the art's visible 24 pixels at `badgeArtUnit` device pixels each,
 * or 0 where there is no art to draw. It is a whole number of device pixels at every scale: k where one fits, the box's
 * own device pixels where none does.
 *
 * @param art Whether the art is drawn (the atlas has decoded and holds the badge's key).
 * @param scale The UI scale.
 * @returns The width in logical pixels.
 * @example
 * badgeWidthFor(true, 2);
 * // 12: 24 device pixels at 2 per logical pixel
 */
export function badgeWidthFor(art: boolean, scale: number): number {
  return art ? (BADGE_ART_CONTENT * badgeArtUnit(BADGE_BOX, scale).unit) / scale : 0;
}

/**
 * Half the vision cap in world pixels on each axis: 13.5 tiles to the side and 7.5 up and down.
 */
const CAP_HALF_WIDTH = (VISION_CAP_TILES.width * WORLD_TILE_PIXELS) / 2;
const CAP_HALF_HEIGHT = (VISION_CAP_TILES.height * WORLD_TILE_PIXELS) / 2;

/**
 * A plate's parts, written in place so a plate laid out every frame allocates nothing: the staff badge's box's, the flag
 * mark's, the name's and the party mark's top left in logical pixels, the plate's width in logical pixels and its box in
 * device pixels, which rises over the name's line when a badge stands on it.
 */
export interface PlateLayout {
  badgeX: number;
  centreY: number;
  flagX: number;
  flagY: number;
  partyX: number;
  partyY: number;
  textX: number;
  textY: number;
  width: number;
  deviceLeft: number;
  deviceTop: number;
  deviceWidth: number;
  deviceHeight: number;
}

/**
 * A layout to write into.
 *
 * @returns A layout of zeros.
 */
export function newPlateLayout(): PlateLayout {
  return { badgeX: 0, centreY: 0, flagX: 0, flagY: 0, partyX: 0, partyY: 0, textX: 0, textY: 0, width: 0, deviceLeft: 0, deviceTop: 0, deviceWidth: 0, deviceHeight: 0 };
}

/**
 * A plate's width: the flag mark, then the name and the party mark each after a gap when the plate draws them, or the
 * flag mark alone when the plate draws neither (Flag only, P1.9-T24), which then stands centred with no gap beside it.
 *
 * @param flagWidth The flag mark's width in logical pixels, 8, or 12 with its lock.
 * @param nameWidth The name's width in logical pixels, 0 for no name.
 * @param partyWidth The party mark's width in logical pixels, 0 for none.
 * @param badgeWidth The staff badge's width (`badgeWidthFor`), 0 for none: it and the gap after it come before the flag mark.
 * @returns The width in logical pixels.
 */
export function plateWidth(flagWidth: number, nameWidth: number, partyWidth = 0, badgeWidth = 0): number {
  return (badgeWidth > 0 ? badgeWidth + FLAG_GAP : 0) + flagWidth + (partyWidth > 0 ? FLAG_GAP + partyWidth : 0) + (nameWidth > 0 ? FLAG_GAP + nameWidth : 0);
}

/**
 * Lays a plate out over its anchor. A plate with no name keeps the name's line where it stands, so its mark sits
 * where it sits beside a name, and only its width and its left edge change.
 *
 * @param anchorX The device pixel the plate is centred on, the character's feet across.
 * @param anchorY The device pixel of the character's head, which the plate stands above.
 * @param scale The UI scale.
 * @param flagWidth The flag mark's width in logical pixels, 8, or 12 with its lock.
 * @param nameWidth The name's width in logical pixels, 0 for a plate that draws the mark alone.
 * @param out Where to write the layout.
 * @param partyWidth The party mark's width in logical pixels, 0 for a plate that draws none.
 * @param badgeWidth The staff badge's width (`badgeWidthFor`), 0 for a plate that draws none: the badge then stands at the
 *   plate's left, level with the flag mark, and the plate rises no higher for it.
 * @returns `out`.
 */
export function plateLayout(anchorX: number, anchorY: number, scale: number, flagWidth: number, nameWidth: number, out: PlateLayout, partyWidth = 0, badgeWidth = 0): PlateLayout {
  const width = plateWidth(flagWidth, nameWidth, partyWidth, badgeWidth);
  const left = anchorX - Math.floor(Math.round(width * scale) / 2);
  const top = anchorY - (ABOVE_HEAD + LINE.height) * scale;
  const markLeft = left / scale + (badgeWidth > 0 ? badgeWidth + FLAG_GAP : 0);
  out.width = width;
  out.badgeX = left / scale;
  out.centreY = top / scale + LINE.baseline - MARK / 2;
  out.flagX = markLeft;
  out.flagY = top / scale + LINE.baseline - MARK;
  out.textX = markLeft + flagWidth + FLAG_GAP;
  out.partyX = nameWidth > 0 ? out.textX + nameWidth + FLAG_GAP : out.textX;
  out.textY = top / scale;
  out.partyY = partyWidth > 0 ? out.textY + Math.floor((LINE.height - partyWidth) / 2) : out.flagY;
  out.deviceLeft = left;
  out.deviceTop = top;
  out.deviceWidth = Math.round(width * scale);
  out.deviceHeight = Math.max(LINE.height, LINE.baseline - MARK + flagWidth) * scale;
  return out;
}

/**
 * Where the parts of a badge are drawn on the canvas, written in place: the art's top left and its scale in logical
 * pixels, and whether the art is drawn smoothed (downscaled off the whole device pixels).
 */
export interface BadgeParts {
  artX: number;
  artY: number;
  artDisplay: number;
  smooth: boolean;
}

/**
 * Parts to write into.
 *
 * @returns Parts of zeros.
 */
export function newBadgeParts(): BadgeParts {
  return { artX: 0, artY: 0, artDisplay: 0, smooth: false };
}

/**
 * Places a badge's parts level with the flag mark (ADR-112 decision 4.4, P1.9-T122): the art is 32 x `badgeArtUnit` device pixels
 * (a whole k = floor(12 d / 24), or the fraction that fits the visible 24 to the box at a scale where none does), set so its visible 24 pixels start at the badge's left and are centred on the flag mark's centre,
 * the transparent inset hanging outside the badge's room.
 * The visible art's edges are whole device pixels; the 32 pixel frame around them is not at a scale where the art is downscaled.
 *
 * @param contentX The badge's left in logical pixels (`PlateLayout.badgeX`).
 * @param centreY The flag mark's centre down (`PlateLayout.centreY`).
 * @param scale The UI scale.
 * @param out Where to write the parts.
 * @returns `out`.
 */
export function badgeParts(contentX: number, centreY: number, scale: number, out: BadgeParts): BadgeParts {
  const { unit, whole } = badgeArtUnit(BADGE_BOX, scale);
  const display = unit / scale;
  out.smooth = !whole;
  out.artDisplay = display;
  out.artX = contentX - BADGE_ART_INSET * display;
  out.artY = centreY - (BADGE_ART_INSET + BADGE_ART_CONTENT / 2) * display;
  return out;
}

/**
 * The atlas frame of a flag mark at 8 and its width.
 */
export interface FlagMarkFrame {
  readonly frame: string;
  readonly width: number;
}

/**
 * The marks by shape, open and locked, made once so choosing one allocates nothing.
 */
const MARKS: Readonly<Record<string, FlagMarkFrame>> = Object.freeze({
  shield: Object.freeze({ frame: "ui/flag/shield_8", width: MARK }),
  pennant: Object.freeze({ frame: "ui/flag/pennant_8", width: MARK }),
  burst: Object.freeze({ frame: "ui/flag/burst_8", width: MARK }),
  "shield-locked": Object.freeze({ frame: "ui/flag/shield-locked_8", width: MARK + 4 }),
  "pennant-locked": Object.freeze({ frame: "ui/flag/pennant-locked_8", width: MARK + 4 }),
  "burst-locked": Object.freeze({ frame: "ui/flag/burst-locked_8", width: MARK + 4 }),
});

/**
 * The flag mark a PvP flag byte shows (Bible 6.1): the shield for Protected, the pennant for Flagged and the
 * burst for Combatant, as the vitals frame's marks are, with the lock when an area holds the state. A state the
 * byte does not name shows as Protected, as the vitals frame reads it.
 *
 * @param byte The flag byte: the state in its low six bits, the switching bit, which this does not read, and the zone lock in its top bit.
 * @returns The mark's atlas frame and width, the same object each time for a byte.
 */
export function flagFrame(byte: number): FlagMarkFrame {
  const state = flagStateOf(byte);
  const shape = state === PvpFlag.Combatant ? "burst" : state === PvpFlag.Flagged ? "pennant" : "shield";
  return MARKS[(byte & PvpFlag.ZoneLocked) !== 0 ? `${shape}-locked` : shape] as FlagMarkFrame;
}

/**
 * Whether a character stands inside the local character's vision cap, `VISION_CAP_TILES` around it (ADR-061
 * decision 6): a nameplate is hidden outside it, since the map server's margin sends entities the player must
 * not see named.
 *
 * @param dx The character's feet across from the local character's, in world pixels.
 * @param dy The same down.
 * @returns True inside the rectangle, its edge included.
 */
export function inVisionCap(dx: number, dy: number): boolean {
  return Math.abs(dx) <= CAP_HALF_WIDTH && Math.abs(dy) <= CAP_HALF_HEIGHT;
}

/**
 * Whether any of a plate's box lies on the canvas.
 *
 * @param left The box's left in device pixels.
 * @param top Its top.
 * @param width Its width.
 * @param height Its height.
 * @param screenWidth The canvas's width in device pixels.
 * @param screenHeight Its height.
 * @returns True when the box overlaps the canvas.
 */
export function onScreen(left: number, top: number, width: number, height: number, screenWidth: number, screenHeight: number): boolean {
  return left < screenWidth && left + width > 0 && top < screenHeight && top + height > 0;
}

/**
 * One drawn plate's hit box in device pixels and the screen height of its character's anchor.
 */
export interface PlateHit {
  readonly handle: number;
  readonly left: number;
  readonly top: number;
  readonly width: number;
  readonly height: number;
  readonly anchorY: number;
}

/**
 * The plate a point holds when plates overlap, as a crowd's do: the one whose character stands lowest on screen,
 * which is the one in front; a tie goes to the plate whose centre is nearest the point, then to the lower handle.
 *
 * @param x The point's x in device pixels.
 * @param y The point's y in device pixels.
 * @param plates The drawn plates.
 * @returns The handle of the plate picked, or -1 when none holds the point.
 */
export function pickPlate(x: number, y: number, plates: readonly PlateHit[]): number {
  let found = -1;
  let foundAnchor = Number.NEGATIVE_INFINITY;
  let foundDistance = Number.POSITIVE_INFINITY;
  for (const plate of plates) {
    if (x < plate.left || x >= plate.left + plate.width || y < plate.top || y >= plate.top + plate.height) {
      continue;
    }
    const dx = x - (plate.left + plate.width / 2);
    const dy = y - (plate.top + plate.height / 2);
    const distance = dx * dx + dy * dy;
    const better = plate.anchorY > foundAnchor || (plate.anchorY === foundAnchor && (distance < foundDistance || (distance === foundDistance && plate.handle < found)));
    if (better) {
      found = plate.handle;
      foundAnchor = plate.anchorY;
      foundDistance = distance;
    }
  }
  return found;
}
